import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Calculator, FileText, Lock, Trophy, TrendingUp, ArrowLeft } from 'lucide-react';

const Dashboard = ({ progress, updateProgress }) => {
  const navigate = useNavigate();

  const stage1Passed = progress?.stage1_passed || false;
  const stage1MathsScore = progress?.stage1_maths_score || null;
  const stage1EnglishScore = progress?.stage1_english_score || null;
  const stage2MathsScore = progress?.stage2_maths_score || null;
  const stage2EnglishScore = progress?.stage2_english_score || null;

  const startExam = (stage, subject) => {
    navigate(`/exam/${stage}/${subject}`);
  };

  const getScoreColor = (score) => {
    if (score === null || score === undefined) return 'text-gray-400';
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBadge = (score) => {
    if (score === null || score === undefined) return null;
    if (score >= 70) return <Badge className="bg-green-500">Passed ✓</Badge>;
    return <Badge variant="destructive">Not Passed</Badge>;
  };

  const overallProgress = () => {
    let completed = 0;
    let total = 4;
    if (stage1MathsScore !== null) completed++;
    if (stage1EnglishScore !== null) completed++;
    if (stage2MathsScore !== null) completed++;
    if (stage2EnglishScore !== null) completed++;
    return (completed / total) * 100;
  };

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <div className="max-w-6xl mx-auto space-y-6 animate-fade-in">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-white hover:bg-white/20"
          data-testid="back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
        </Button>
        
        {/* Header */}
        <Card className="shadow-xl border-0" data-testid="dashboard-header">
          <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-lg">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-3xl font-bold mb-2">Your Learning Journey</CardTitle>
                <CardDescription className="text-white/90">
                  Level: <span className="font-semibold capitalize">{progress?.competency_level || 'Not Set'}</span>
                </CardDescription>
              </div>
              <Trophy className="h-12 w-12 text-yellow-300" />
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 font-medium">Overall Progress</span>
                <span className="font-bold text-purple-600">{Math.round(overallProgress())}%</span>
              </div>
              <Progress value={overallProgress()} className="h-3" />
              <p className="text-xs text-gray-500 mt-2">
                Complete all Stage 1 exams with 70%+ to unlock Stage 2
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Stage 1 */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="bg-white p-3 rounded-lg shadow-md">
              <span className="text-2xl font-bold text-purple-600">Stage 1</span>
            </div>
            <div className="flex-1 h-1 bg-white/50 rounded"></div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="shadow-lg hover:shadow-xl transition-all border-2 hover:border-purple-400" data-testid="stage1-maths-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <Calculator className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">Mathematics</CardTitle>
                      <CardDescription>25 Questions</CardDescription>
                    </div>
                  </div>
                  {getScoreBadge(stage1MathsScore)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {stage1MathsScore !== null ? (
                  <div className="text-center py-4">
                    <div className={`text-4xl font-bold ${getScoreColor(stage1MathsScore)}`}>
                      {stage1MathsScore}%
                    </div>
                    <p className="text-sm text-gray-500 mt-2">Your Score</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">Test your mathematical skills with 25 challenging questions.</p>
                )}
                <Button
                  onClick={() => startExam(1, 'maths')}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                  data-testid="start-stage1-maths"
                >
                  {stage1MathsScore !== null ? 'Retake Test' : 'Start Maths Test'}
                </Button>
              </CardContent>
            </Card>

            <Card className="shadow-lg hover:shadow-xl transition-all border-2 hover:border-purple-400" data-testid="stage1-english-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 p-3 rounded-lg">
                      <FileText className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">English</CardTitle>
                      <CardDescription>25 Questions</CardDescription>
                    </div>
                  </div>
                  {getScoreBadge(stage1EnglishScore)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {stage1EnglishScore !== null ? (
                  <div className="text-center py-4">
                    <div className={`text-4xl font-bold ${getScoreColor(stage1EnglishScore)}`}>
                      {stage1EnglishScore}%
                    </div>
                    <p className="text-sm text-gray-500 mt-2">Your Score</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">Master grammar, vocabulary, and comprehension skills.</p>
                )}
                <Button
                  onClick={() => startExam(1, 'english')}
                  className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                  data-testid="start-stage1-english"
                >
                  {stage1EnglishScore !== null ? 'Retake Test' : 'Start English Test'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Stage 2 */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className={`bg-white p-3 rounded-lg shadow-md ${!stage1Passed && 'opacity-50'}`}>
              <span className="text-2xl font-bold text-indigo-600">Stage 2</span>
            </div>
            <div className="flex-1 h-1 bg-white/50 rounded"></div>
            {!stage1Passed && (
              <Badge variant="secondary" className="bg-white/90">
                <Lock className="h-3 w-3 mr-1" /> Locked
              </Badge>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Card className={`shadow-lg ${stage1Passed ? 'hover:shadow-xl border-2 hover:border-indigo-400' : 'opacity-60'} transition-all`} data-testid="stage2-maths-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-purple-100 p-3 rounded-lg">
                      <Calculator className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">Advanced Mathematics</CardTitle>
                      <CardDescription>25 Questions</CardDescription>
                    </div>
                  </div>
                  {stage1Passed && getScoreBadge(stage2MathsScore)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {stage2MathsScore !== null ? (
                  <div className="text-center py-4">
                    <div className={`text-4xl font-bold ${getScoreColor(stage2MathsScore)}`}>
                      {stage2MathsScore}%
                    </div>
                    <p className="text-sm text-gray-500 mt-2">Your Score</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">
                    {stage1Passed ? 'Advanced mathematical concepts and problem-solving.' : 'Complete Stage 1 to unlock'}
                  </p>
                )}
                <Button
                  onClick={() => startExam(2, 'maths')}
                  disabled={!stage1Passed}
                  className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:opacity-50"
                  data-testid="start-stage2-maths"
                >
                  {!stage1Passed ? (
                    <><Lock className="h-4 w-4 mr-2" /> Locked</>
                  ) : stage2MathsScore !== null ? 'Retake Test' : 'Start Maths Test'}
                </Button>
              </CardContent>
            </Card>

            <Card className={`shadow-lg ${stage1Passed ? 'hover:shadow-xl border-2 hover:border-indigo-400' : 'opacity-60'} transition-all`} data-testid="stage2-english-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-teal-100 p-3 rounded-lg">
                      <FileText className="h-6 w-6 text-teal-600" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">Advanced English</CardTitle>
                      <CardDescription>25 Questions</CardDescription>
                    </div>
                  </div>
                  {stage1Passed && getScoreBadge(stage2EnglishScore)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {stage2EnglishScore !== null ? (
                  <div className="text-center py-4">
                    <div className={`text-4xl font-bold ${getScoreColor(stage2EnglishScore)}`}>
                      {stage2EnglishScore}%
                    </div>
                    <p className="text-sm text-gray-500 mt-2">Your Score</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">
                    {stage1Passed ? 'Complex grammar, advanced vocabulary, and comprehension.' : 'Complete Stage 1 to unlock'}
                  </p>
                )}
                <Button
                  onClick={() => startExam(2, 'english')}
                  disabled={!stage1Passed}
                  className="w-full bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 disabled:opacity-50"
                  data-testid="start-stage2-english"
                >
                  {!stage1Passed ? (
                    <><Lock className="h-4 w-4 mr-2" /> Locked</>
                  ) : stage2EnglishScore !== null ? 'Retake Test' : 'Start English Test'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
